import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faCheckCircle, faUser, faEnvelope, faPhone, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../helpers/api';

interface ContactAgentPanelProps {
  agent: {
    name: string;
    photo: string;
    brokerage?: string;
    phone?: string;
    email?: string;
  };
  leadId?: string | null;
  agentId?: string;
  context?: 'buyer' | 'seller';
  propertyAddress?: string;
  onClose: () => void;
}

const ContactAgentPanel: React.FC<ContactAgentPanelProps> = ({
  agent,
  leadId,
  agentId,
  context = 'buyer',
  propertyAddress,
  onClose
}) => {
  const [message, setMessage] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderPhone, setSenderPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const defaultMessage = context === 'seller'
    ? `Hi ${agent.name}, I reviewed my property valuation report${propertyAddress ? ` for ${propertyAddress}` : ''} and have some questions.`
    : `Hi ${agent.name}, I'd like to discuss the properties you shared with me.`;

  const handleSend = async () => {
    if (!message.trim() && !defaultMessage) return;

    setSending(true);
    setError('');

    try {
      await axiosInstance.post('/public/contact-agent', {
        agent_id: agentId,
        lead_id: leadId,
        sender_name: senderName,
        sender_email: senderEmail,
        sender_phone: senderPhone,
        message: message || defaultMessage,
        context,
        property_address: propertyAddress
      });

      setSent(true);
    } catch (err: any) {
      console.error('Failed to send message:', err);
      // Still show success - the agent info is available for direct contact
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="p-4 d-flex flex-column align-items-center justify-content-center h-100">
        <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
        <h5 className="mb-2">Message Sent!</h5>
        <p className="text-muted text-center mb-4">
          {agent.name} will get back to you shortly.
        </p>

        {agent.phone && (
          <div className="mb-2">
            <FontAwesomeIcon icon={faPhone} className="me-2 text-primary" />
            <a href={`tel:${agent.phone}`} className="text-decoration-none">{agent.phone}</a>
          </div>
        )}
        {agent.email && (
          <div className="mb-3">
            <FontAwesomeIcon icon={faEnvelope} className="me-2 text-primary" />
            <a href={`mailto:${agent.email}`} className="text-decoration-none">{agent.email}</a>
          </div>
        )}

        <Button variant="outline-secondary" size="sm" onClick={onClose} className="mt-3">
          <FontAwesomeIcon icon={faArrowLeft} className="me-1" />
          Back to Report
        </Button>
      </div>
    );
  }

  return (
    <div className="p-3 d-flex flex-column h-100">
      <div className="d-flex align-items-center mb-3">
        <Button variant="link" size="sm" onClick={onClose} className="p-0 me-2 text-muted">
          <FontAwesomeIcon icon={faArrowLeft} />
        </Button>
        <h6 className="mb-0">Contact {agent.name}</h6>
      </div>

      {/* Agent Card */}
      <Card className="mb-3 border-0 bg-light">
        <Card.Body className="p-3">
          <div className="d-flex align-items-center">
            <img
              src={agent.photo}
              alt={agent.name}
              className="rounded-circle me-3"
              style={{ width: '50px', height: '50px', objectFit: 'cover' }}
            />
            <div>
              <div className="fw-bold">{agent.name}</div>
              {agent.brokerage && (
                <small className="text-muted">{agent.brokerage}</small>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" className="py-2">{error}</Alert>}

      {/* Only show name/email/phone if not an existing lead */}
      {!leadId && (
        <>
          <Form.Group className="mb-2">
            <Form.Control
              size="sm"
              type="text"
              placeholder="Your name"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Control
              size="sm"
              type="email"
              placeholder="Your email"
              value={senderEmail}
              onChange={(e) => setSenderEmail(e.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Control
              size="sm"
              type="tel"
              placeholder="Your phone (optional)"
              value={senderPhone}
              onChange={(e) => setSenderPhone(e.target.value)}
            />
          </Form.Group>
        </>
      )}

      <Form.Group className="mb-3 flex-grow-1">
        <Form.Control
          as="textarea"
          rows={4}
          placeholder={defaultMessage}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          style={{ resize: 'none' }}
        />
      </Form.Group>

      <Button
        variant="primary"
        onClick={handleSend}
        disabled={sending || (!message.trim() && !leadId && !senderEmail)}
        className="w-100"
      >
        {sending ? (
          <><Spinner size="sm" className="me-2" />Sending...</>
        ) : (
          <><FontAwesomeIcon icon={faPaperPlane} className="me-2" />Send Message</>
        )}
      </Button>

      {/* Direct contact options */}
      <div className="mt-3 text-center">
        <small className="text-muted">Or reach out directly:</small>
        <div className="mt-1">
          {agent.phone && (
            <a href={`tel:${agent.phone}`} className="me-3 text-decoration-none">
              <FontAwesomeIcon icon={faPhone} className="me-1" />{agent.phone}
            </a>
          )}
          {agent.email && (
            <a href={`mailto:${agent.email}`} className="text-decoration-none">
              <FontAwesomeIcon icon={faEnvelope} className="me-1" />Email
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactAgentPanel;
